import { Link } from "react-router-dom";
import { BookOpen, LayoutGrid, List, Search, Square } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import type { ReactNode } from "react";
import { useTrail, useSpring, animated, to } from "@react-spring/web";
import { progressFromEvents } from "@/components/organisms/studio/AgentRunProgress";
import { Badge } from "@/components/atoms/studio/ui/badge";
import { skillImpact } from "@/lib/studio/impact";
import { useSkills, useRuns } from "@/hooks/studio-platform";
import { useRunStore, useAnyRunActive, getRunStore } from "@/stores/studio/runStore";
import type { RunSummary, RunStatusValue, SkillSummary } from "@/services/studio/types";
import { formatRelative } from "@/lib/studio/utils";

function durationLabel(ms: number | null | undefined): string {
  if (!ms || !Number.isFinite(ms) || ms <= 0) return "";
  if (ms < 1000) return `${Math.round(ms)}ms`;
  return `${(ms / 1000).toFixed(1)}s`;
}

// ── Status dot ────────────────────────────────────────────────────────────────
function StatusDot({ status }: { status?: RunStatusValue | string | null }) {
  if (status === "running") {
    return (
      <span className="relative flex h-2 w-2 shrink-0">
        <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-[hsl(var(--success))] opacity-60" />
        <span className="relative inline-flex h-2 w-2 rounded-full bg-[hsl(var(--success))]" />
      </span>
    );
  }
  if (status === "done" || status === "cached") {
    return <span className="h-2 w-2 shrink-0 rounded-full bg-[hsl(var(--success)/0.6)]" />;
  }
  if (status === "error" || status === "stopped") {
    return <span className="h-2 w-2 shrink-0 rounded-full bg-[hsl(var(--destructive)/0.7)]" />;
  }
  return <span className="h-2 w-2 shrink-0 rounded-full bg-muted-foreground/20" />;
}

// ── Impact badge ──────────────────────────────────────────────────────────────
// What the skill DOES to the org — the decision-relevant signal (replaces the old
// meaningless "Better/Good" quality verdict).
function ImpactBadge({ category }: { category?: string | null }) {
  const { label, writes } = skillImpact(category);
  return <Badge variant={writes ? "warm" : "secondary"}>{label}</Badge>;
}

// ── Page ──────────────────────────────────────────────────────────────────────
export function BrowseSkills() {
  const [query, setQuery] = useState("");
  const [showListView, setShowListView] = useState<boolean>(() => {
    return localStorage.getItem("migrations-view-preference") !== "grid";
  });

  const toggleView = () => {
    setShowListView((prev) => {
      const next = !prev;
      localStorage.setItem("migrations-view-preference", next ? "list" : "grid");
      return next;
    });
  };

  const skillsQ = useSkills();
  const runsQ = useRuns({ limit: 100 });

  const skills = skillsQ.data?.skills ?? [];

  // Build skillId → most-recent run map
  const runsBySkillId = useMemo<Map<string, RunSummary>>(() => {
    const map = new Map<string, RunSummary>();
    for (const run of runsQ.data?.runs ?? []) {
      if (run.skill_id && !map.has(run.skill_id)) map.set(run.skill_id, run);
    }
    return map;
  }, [runsQ.data?.runs]);

  // Reactive "is any skill running?" across all per-skill run stores (replaces the
  // old singleton status read). Row/card components subscribe to their own slice.
  const anyStoreRunning = useAnyRunActive();

  // Poll every 3s while any skill is actively running
  const hasRunning = useMemo(
    () => [...runsBySkillId.values()].some((r) => r.status === "running") || anyStoreRunning,
    [runsBySkillId, anyStoreRunning],
  );
  useEffect(() => {
    if (!hasRunning) return;
    const id = setInterval(() => { void runsQ.refetch(); }, 3000);
    return () => clearInterval(id);
  }, [hasRunning]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return skills;
    return skills.filter((skill) =>
      skill.title.toLowerCase().includes(q) ||
      skill.id.toLowerCase().includes(q) ||
      skill.description.toLowerCase().includes(q) ||
      (skill.tools_used ?? []).some((t) => t.toLowerCase().includes(q))
    );
  }, [skills, query]);

  // Row entrance trail
  const trail = useTrail(filtered.length, {
    from: { opacity: 0, transform: "translateY(6px)" },
    to: { opacity: 1, transform: "translateY(0px)" },
    config: { tension: 340, friction: 28 },
  });

  const skeletonTrail = useTrail(SKELETON_WIDTHS.length, {
    from: { opacity: 0, transform: "translateY(6px)" },
    to: { opacity: 1, transform: "translateY(0px)" },
    config: { tension: 340, friction: 28 },
  });

  return (
    <div className="flex h-full flex-col">
      {/* Sticky header */}
      <div className="shrink-0 pb-3">
        {/* Title row */}
        <div className="py-1 pb-4">
          <h1 className="text-2xl font-bold tracking-tight">Migrations</h1>
          <p className="mt-0.5 text-sm text-muted-foreground">
            Reusable automation templates that run against your connected org.
          </p>
        </div>

        {/* Search bar + view toggle */}
        <div className="flex items-center gap-2.5 rounded-lg border border-border/70 bg-muted/20 px-3 py-2 transition-colors focus-within:border-primary/40 focus-within:bg-muted/30">
          <Search className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search by name, description or tool…"
            className="flex-1 bg-transparent text-sm outline-none placeholder:text-muted-foreground/40"
          />
          {skills.length > 0 && !query && (
            <span className="shrink-0 font-mono text-[11px] text-muted-foreground/50">
              {skills.length} migrations
            </span>
          )}
          {query && (
            <button
              type="button"
              onClick={() => setQuery("")}
              className="shrink-0 font-mono text-[11px] text-muted-foreground transition-colors hover:text-foreground"
            >
              {filtered.length} found · clear
            </button>
          )}
          <span className="mx-0.5 h-4 w-px shrink-0 bg-border/60" />
          <HoverTooltip label={showListView ? "Grid view" : "List view"}>
            <button
              type="button"
              onClick={toggleView}
              className="shrink-0 rounded p-1 text-muted-foreground transition-colors hover:bg-muted/60 hover:text-foreground"
            >
              {showListView ? <LayoutGrid className="h-3.5 w-3.5" /> : <List className="h-3.5 w-3.5" />}
            </button>
          </HoverTooltip>
        </div>
      </div>

      {/* Scrollable content */}
      <div className="flex-1 overflow-y-auto">
        {!showListView ? (
          /* Grid view */
          skillsQ.isLoading ? (
            <PlaybookCardGridSkeleton />
          ) : filtered.length === 0 ? null : (
            <PlaybookCardGrid skills={filtered} runsBySkillId={runsBySkillId} />
          )
        ) : skillsQ.isLoading ? (
          <div className="overflow-hidden rounded-xl border border-border bg-card">
            {skeletonTrail.map((style, i) => (
              <animated.div key={i} style={style}>
                <SkeletonRow
                  nameWidth={SKELETON_WIDTHS[i].name}
                  metaWidth={SKELETON_WIDTHS[i].meta}
                  isLast={i === SKELETON_WIDTHS.length - 1}
                />
              </animated.div>
            ))}
          </div>
        ) : skillsQ.isError ? (
          <p className="font-mono text-sm text-destructive">
            {(skillsQ.error as Error)?.message ?? "Could not load migrations."}
          </p>
        ) : filtered.length === 0 ? (
          <div className="flex flex-col items-center py-24 text-center">
            <BookOpen className="mb-4 h-12 w-12 text-muted-foreground/20" />
            <p className="font-semibold">No migrations found</p>
            <p className="mt-1 text-sm text-muted-foreground">
              {query ? "Try a different search or clear the filter." : "No migrations available."}
            </p>
          </div>
        ) : (
          /* List view */
          <div className="overflow-hidden rounded-xl border border-border bg-card">
            {trail.map((style, idx) => {
              const skill = filtered[idx];
              return (
                <animated.div key={skill.id} style={style}>
                  <PlaybookRow
                    skill={skill}
                    latestRun={runsBySkillId.get(skill.id)}
                    isLast={idx === filtered.length - 1}
                  />
                </animated.div>
              );
            })}
          </div>
        )}
      </div>

    </div>
  );
}

// ── Skeleton ──────────────────────────────────────────────────────────────────
const SKELETON_WIDTHS = [
  { name: "w-52", meta: "w-32" },
  { name: "w-40", meta: "w-24" },
  { name: "w-64", meta: "w-36" },
  { name: "w-44", meta: "w-28" },
  { name: "w-56", meta: "w-32" },
  { name: "w-48", meta: "w-20" },
] as const;

function SkeletonRow({ nameWidth, metaWidth, isLast }: { nameWidth: string; metaWidth: string; isLast: boolean }) {
  return (
    <div className={["flex items-center gap-4 px-5 py-3.5", !isLast ? "border-b border-border/60" : ""].join(" ")}>
      <div className="h-2 w-2 shrink-0 rounded-full bg-muted-foreground/15" />
      <div className="flex-1 min-w-0 space-y-1.5">
        <div className={["h-3.5 rounded-md bg-muted-foreground/10 loading-parallax", nameWidth].join(" ")} />
        <div className={["h-2.5 rounded-md bg-muted-foreground/[0.07] loading-parallax", metaWidth].join(" ")} />
      </div>
      <div className="h-5 w-20 shrink-0 rounded-full bg-muted-foreground/10 loading-parallax" />
      <div className="hidden sm:flex w-auto max-w-[9rem] shrink-0 justify-end">
        <div className="h-2.5 w-24 rounded-md bg-muted-foreground/[0.07] loading-parallax" />
      </div>
    </div>
  );
}

// ── Playbook Row — subscribes to runStore directly (same pattern as AgentRow) ──
function PlaybookRow({
  skill,
  latestRun,
  isLast,
}: {
  skill: SkillSummary;
  latestRun: RunSummary | undefined;
  isLast: boolean;
}) {
  // This skill's own run store — subscribe only to the slices this row needs.
  const runStatus = useRunStore(skill.id, (s) => s.status);
  const runEvents = useRunStore(skill.id, (s) => s.events);

  const isActivelyRunning = runStatus === "running";
  const progress = isActivelyRunning ? progressFromEvents(runEvents).percent : 0;
  const displayRunning = isActivelyRunning || latestRun?.status === "running";

  return (
    <div
      className={[
        "relative group flex items-center gap-4 px-5 py-3.5 transition-colors hover:bg-muted/25",
        displayRunning ? "bg-[hsl(var(--success)/0.03)]" : "",
        !isLast ? "border-b border-border/60" : "",
      ].join(" ")}
    >
      {/* Status dot */}
      <StatusDot status={displayRunning ? "running" : latestRun?.status} />

      {/* Name + meta */}
      <Link to={`/migrations/${encodeURIComponent(skill.id)}`} className="min-w-0 flex-1 focus:outline-none">
        <div className="flex items-baseline gap-2">
          <span className="truncate font-semibold text-sm text-foreground group-hover:text-primary transition-colors">
            {skill.title}
          </span>
        </div>
        <div className="mt-0.5 flex items-center gap-2 font-mono text-[11px] text-muted-foreground">
          <span>{(skill.tools_used ?? []).length} tools</span>
          {skill.category && (
            <>
              <span className="opacity-30">·</span>
              <span className="capitalize">{skill.category}</span>
            </>
          )}
        </div>
      </Link>

      <ImpactBadge category={skill.category} />

      {/* Last run status — hidden while actively running */}
      {!isActivelyRunning && (
        <div className="hidden sm:block w-auto max-w-[9rem] shrink-0 text-right">
          <RunStatus run={latestRun} />
        </div>
      )}

      {/* Stop button — only visible while this skill is actively running */}
      {isActivelyRunning && (
        <button
          type="button"
          onClick={(e) => {
            e.preventDefault();
            getRunStore(skill.id).getState().abort();
          }}
          className="inline-flex shrink-0 items-center gap-1.5 rounded-md border border-destructive/50 px-2.5 py-1 font-mono text-[11px] text-destructive transition-colors hover:bg-destructive/10"
        >
          <Square className="h-2.5 w-2.5" />
          Stop
        </button>
      )}

      {/* Live progress bar at the bottom of the row */}
      {isActivelyRunning && (
        <div className="absolute bottom-0 left-0 right-0 h-[2px] overflow-hidden">
          <div
            className="h-full bg-[hsl(var(--success))] transition-all duration-500"
            style={{ width: `${Math.max(progress, 3)}%` }}
          />
        </div>
      )}
    </div>
  );
}

// ── Inline run status ─────────────────────────────────────────────────────────
function RunStatus({ run }: { run: RunSummary | undefined }) {
  if (!run) return null;
  if (run.status === "running") {
    return (
      <span className="font-mono text-[11px] text-[hsl(var(--success))]">
        Running · {formatRelative(run.started_at)}
      </span>
    );
  }
  if (run.status === "done" || run.status === "cached") {
    const dur = durationLabel(run.total_ms);
    return (
      <span className="font-mono text-[11px] text-muted-foreground">
        {dur ? `${dur} · ` : ""}{formatRelative(run.started_at)}
      </span>
    );
  }
  if (run.status === "error") {
    return (
      <span className="font-mono text-[11px] text-[hsl(var(--destructive)/0.8)]">
        Error · {formatRelative(run.started_at)}
      </span>
    );
  }
  if (run.status === "stopped") {
    return (
      <span className="font-mono text-[11px] text-muted-foreground">
        Stopped · {formatRelative(run.started_at)}
      </span>
    );
  }
  return (
    <span className="font-mono text-[11px] text-muted-foreground">
      {run.status} · {formatRelative(run.started_at)}
    </span>
  );
}

// ── Card grid ─────────────────────────────────────────────────────────────────
function PlaybookCardGrid({
  skills,
  runsBySkillId,
}: {
  skills: SkillSummary[];
  runsBySkillId: Map<string, RunSummary>;
}) {
  const trail = useTrail(skills.length, {
    from: { opacity: 0, transform: "translateY(10px)" },
    to: { opacity: 1, transform: "translateY(0px)" },
    config: { tension: 300, friction: 28 },
  });

  return (
    <div className="grid gap-4 md:grid-cols-2">
      {trail.map((style, idx) => (
        <animated.div key={skills[idx].id} style={style} className="h-full min-w-0">
          <PlaybookCard skill={skills[idx]} latestRun={runsBySkillId.get(skills[idx].id)} />
        </animated.div>
      ))}
    </div>
  );
}

// ── Playbook Card — subscribes to runStore directly (same pattern as AgentCard) ─
function PlaybookCard({
  skill,
  latestRun,
}: {
  skill: SkillSummary;
  latestRun: RunSummary | undefined;
}) {
  // This skill's own run store — fine-grained per-skill selectors.
  const runStatus = useRunStore(skill.id, (s) => s.status);
  const runEvents = useRunStore(skill.id, (s) => s.events);

  const isActivelyRunning = runStatus === "running";
  const progress = isActivelyRunning ? progressFromEvents(runEvents).percent : 0;
  const displayRunning = isActivelyRunning || latestRun?.status === "running";

  const [springs, api] = useSpring(() => ({
    shadow: 0,
    config: { tension: 300, friction: 26 },
  }));

  return (
    <animated.div
      style={{
        boxShadow: springs.shadow.to(
          (s) => `inset 0 1px 0 rgba(255,255,255,0.04), 0 ${1 + s * 8}px ${3 + s * 18}px rgba(0,0,0,${0.05 + s * 0.09})`
        ),
      }}
      onMouseEnter={() => api.start({ shadow: 1 })}
      onMouseLeave={() => api.start({ shadow: 0 })}
      className={[
        "studio-card relative flex h-full flex-col p-4",
        displayRunning ? "bg-[hsl(var(--success)/0.03)]" : "",
      ].join(" ")}
    >
      <Link to={`/migrations/${encodeURIComponent(skill.id)}`} className="flex min-w-0 flex-col gap-2.5 focus:outline-none">
        {/* Header row */}
        <div className="flex items-start justify-between gap-3">
          <div className="flex min-w-0 items-center gap-2">
            <StatusDot status={displayRunning ? "running" : latestRun?.status} />
            <span className="truncate font-semibold text-sm leading-snug text-foreground hover:text-primary transition-colors">
              {skill.title}
            </span>
          </div>
          <ImpactBadge category={skill.category} />
        </div>

        {/* Description */}
        {skill.description && (
          <p className="line-clamp-2 text-xs text-muted-foreground leading-relaxed">
            {skill.description}
          </p>
        )}

        {/* Footer metrics */}
        <div className="mt-auto flex items-center gap-3 font-mono text-[10px] text-muted-foreground">
          <span>{(skill.tools_used ?? []).length} tools</span>
          {skill.category && (
            <>
              <span className="opacity-30">·</span>
              <span className="capitalize">{skill.category}</span>
            </>
          )}
          {latestRun && !isActivelyRunning && latestRun.status !== "running" && (
            <>
              <span className="opacity-30">·</span>
              <span>{formatRelative(latestRun.started_at)}</span>
            </>
          )}
        </div>
      </Link>

      {/* Stop button — absolute-positioned, same as AgentCard */}
      {isActivelyRunning && (
        <button
          type="button"
          onClick={(e) => {
            e.preventDefault();
            getRunStore(skill.id).getState().abort();
          }}
          className="absolute bottom-3 right-3 inline-flex items-center gap-1 rounded-md border border-destructive/50 bg-card px-2 py-0.5 font-mono text-[10px] text-destructive transition-colors hover:bg-destructive/10"
        >
          <Square className="h-2 w-2" />
          Stop
        </button>
      )}

      {/* Live progress bar at the bottom of the card */}
      {isActivelyRunning && (
        <div className="absolute bottom-0 left-0 right-0 h-[2px] overflow-hidden rounded-b-xl">
          <div
            className="h-full bg-[hsl(var(--success))] transition-all duration-500"
            style={{ width: `${Math.max(progress, 3)}%` }}
          />
        </div>
      )}
    </animated.div>
  );
}

const CARD_SKELETON_WIDTHS = ["w-44", "w-36", "w-52", "w-40"] as const;

function PlaybookCardGridSkeleton() {
  const trail = useTrail(4, {
    from: { opacity: 0, transform: "translateY(10px)" },
    to: { opacity: 1, transform: "translateY(0px)" },
    config: { tension: 300, friction: 28 },
  });
  return (
    <div className="grid gap-4 md:grid-cols-2">
      {trail.map((style, idx) => (
        <animated.div key={idx} style={style} className="studio-card flex flex-col gap-2.5 p-4">
          <div className="flex items-center justify-between gap-3">
            <div className="flex min-w-0 items-center gap-2">
              <div className="h-2 w-2 shrink-0 rounded-full bg-muted-foreground/15" />
              <div className={["h-3.5 rounded-md bg-muted-foreground/10 loading-parallax", CARD_SKELETON_WIDTHS[idx]].join(" ")} />
            </div>
            <div className="h-5 w-[60px] shrink-0 rounded-full bg-muted-foreground/10 loading-parallax" />
          </div>
          <div className="space-y-1.5">
            <div className="h-2.5 w-full rounded bg-muted-foreground/[0.07] loading-parallax" />
            <div className="h-2.5 w-3/4 rounded bg-muted-foreground/[0.05] loading-parallax" />
          </div>
          <div className="mt-auto flex items-center gap-2">
            <div className="h-2 w-12 rounded bg-muted-foreground/[0.07] loading-parallax" />
            <div className="h-1.5 w-1.5 shrink-0 rounded-full bg-muted-foreground/10" />
            <div className="h-2 w-16 rounded bg-muted-foreground/[0.07] loading-parallax" />
          </div>
        </animated.div>
      ))}
    </div>
  );
}

// ── HoverTooltip ──────────────────────────────────────────────────────────────
function HoverTooltip({ label, children }: { label: string; children: ReactNode }) {
  const [visible, setVisible] = useState(false);
  const springs = useSpring({
    opacity: visible ? 1 : 0,
    y: visible ? 0 : -4,
    scale: visible ? 1 : 0.9,
    config: { tension: 420, friction: 24 },
  });
  return (
    <div
      className="relative"
      onMouseEnter={() => setVisible(true)}
      onMouseLeave={() => setVisible(false)}
    >
      {children}
      <animated.div
        className="pointer-events-none absolute top-full right-0 z-50 mt-2"
        style={{
          transform: to(
            [springs.y, springs.scale],
            (y, s) => `translateY(${y}px) scale(${s})`,
          ),
          opacity: springs.opacity,
        }}
      >
        <div className="whitespace-nowrap rounded-lg bg-foreground/90 px-2.5 py-1 font-mono text-[11px] font-medium text-background shadow-[0_2px_8px_hsl(var(--foreground)/0.15)]">
          {label}
        </div>
      </animated.div>
    </div>
  );
}
